import { useAppHandoff } from "~/components/participation/useAppHandoff";
import {
  MetaFunction,
  data,
  LoaderFunctionArgs,
  HeadersFunction,
  Link,

  Outlet,
  ShouldRevalidateFunction,
  useLoaderData,
  useRevalidator,
  useSearchParams,
} from "react-router";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import { FiCalendar, FiChevronRight, FiMapPin } from "react-icons/fi";
import { GoDotFill } from "react-icons/go";
import { READ_BOOKING, PURCHASER_BOOKING } from "~/routes/api.events.$eventId.booking";
import { useUser } from "~/hooks/use-user";
import { getFragmentData, gql } from "~/__generated__/graphql";
import {

  EventFragmentDoc,
  EventType,
} from "~/__generated__/graphql/graphql";
import { createApolloClient } from "~/.server/apollo";

import { useEffect, useState } from "react";

import { useAction } from "~/hooks/use-action";

import { CTADialog } from "~/components/CTADialog";
import { GuestList } from "./GuestList";
import { PlanMap } from "./PlanMap";
import { ImageCarousel } from "~/components/ImageCarousel";
import GoldenButterImage from "~/images/golden-butter.png";
import RedFlowerImage from "~/images/red-flower.png";
import GreenFlowerImage from "~/images/green-flower.png";
import { EventInsight } from "./EventInsight";
import { WhatToExpectPills } from "./WhatToExpectPills";
import { HostCards } from "./SidebarCards";
import { ActivityFeed } from "./ActivityFeed";
import { TicketInclusions } from "./TicketInclusions";
import { UrgencyPanel } from "./UrgencyPanel";
import { BottomCTA } from "./BottomCTA";
import { CheckoutDialog } from "./CheckoutDialog";
import { TicketStatusDialog, type TicketStatus } from "./TicketStatusDialog";
import { SectionHeading } from "~/components/SectionHeading";
import { PillButton } from "~/components/PillButton";
import { Text } from "~/components/Text";
import { PrimaryButton } from "~/components/Button";
import { Avatar } from "~/components/Avatar";
import { RichDescription } from "~/components/RichDescription";

import { formatEventDateTime, formatLocalizedDate } from "~/utils/date";
import { isDesktopDevice } from "~/utils/device";
import { track } from "~/utils/analytics";
import {
  buildAppsFlyerLink,
  getUTMParamsWithSession,
} from "~/.server/appsflyer";
import { commitSession } from "~/.server/session";
import { USER_EXISTS_QUERY } from "~/services/graphql/user";
import { getStripePromise } from "~/utils/stripe";
import { buildEventJsonLd } from "./eventSchema";
import { useRootLocale, useRootPosthogBootstrap } from "~/root";
import posthog from "posthog-js";
import {

  formatPlacesLeft,
  getRegionalCopy,

} from "~/utils/localization";
import {

  getSelectedTicketVariation,
  TICKET_VARIATION_PARAM,
} from "./ticketVariations";
import { isTicketCheckoutAvailable } from "./ticketSalesAvailability";
import { isActivePurchaserGroupBooking } from "./purchaser-booking-state";

export const EVENT_FRAGMENT = gql(`
  fragment Event on Event {
    id
    title
    description
    richDescription
    linkPreviews {
      id
      url
      title
      description
      imageUrl
    }
    bannerImageUrl
    bannerImageUrls
    startDate
    endDate
    type
    capacity
    unclaimedGuests(first: 9) {
      pageInfo { totalSize }
      edges { node { id name } }
    }
    waitlists
    approveGuests
    allowGroupRSVPs
    allowGroupDiscountCodes
    maxTicketsPerPurchase
    isAttending
    isHosting
    formInputs {
      id
      label
      placeholder
      type
      selectOptions
    }
    myRegistration {
    id eventID purchaseWalletIntentID originalQuantity
    purchaser { id }
    status { approvalRequired requestState submittedAt decisionExpiresAt approvedAt reservedAt confirmedAt cancelledAt cancellationReason }
    payment { state currency heldAmountCents paidAmountCents refundedAmountCents pendingRefundAmountCents paymentReleasePending }
    reservations {
      id position isPurchaser recipientName claimState state
      user { name profileImageUrl }
      variation { id name emoji }
      ticketCost { totalCostCents currency }
      formComplete
    }
    }
    myRequest {
      id
      state
      submittedAt
      reservation {
        id
        isPurchaser
        state
        profileComplete
        formComplete
        detailsCompletedAt
        registrationStatus { approvalRequired }
      }
      decisionExpiresAt
      resolutionReason
      form {
        value
        formQuestionID
        selectedValues
        formQuestionLabel
        formQuestionType
        formQuestionOptions
      }
      ticketPurchase {
        totalCostCents
        state
        variation {
          id
          name
        }
      }
      ticketPurchaseCode {
        code
      }
    }
    tone
    structure
    hasEnded
    canceled
    hiddenReason
    headline
    isPublished
    publishedAt
    ticketInclusions {
      id
      text
      position
    }
    regularTicketCost {
        totalCostCents
    }
    earlyBirdTicketCost {
        totalCostCents
    }
    ticketVariations {
      id
      name
      emoji
      description
      priceCents
      totalCostCents
      position
      ticketsRemaining
      available
      checkoutStatus
      availableAfterID
      activatedAt
      archivedAt
      availabilityEndsAt
    }
    location: place {
      isGeneralArea
      exactDetailsHidden
      name
      suburb
      city
      state
      country
      postcode
      formattedAddress
      coordinates {
        latitude
        longitude
      }
    }
    club {
      id
      name
      imageUrl
      headline
      premium
      foundingMember
    }
    hosts {
      primary
      user {
        id
        name
        firstName
        age
        bio
        headline
        suburb
        profileImageUrl
        foundingMember
      }
    }
    attendees(first: 1000) {
      edges {
        node {
          id
          user {
            id
            firstName
            profileImageUrl
          }
        }
      }
      pageInfo {
        totalSize
      }
    }
    requests(first: 1000) {
      edges {
        node {
          type
          accepted
        }
      }
    }
    menuSample
    insight {
      name
      title
      body
    }
    similarEvents {
      id
      title
      startDate
      bannerImageUrl
      type
      capacity
      waitlists
      location: place {
        suburb
        city
        state
      }
      hosts {
        primary
        user {
          id
          name
          profileImageUrl
        }
      }
      attendees(first: 3) {
        edges {
          node {
            id
            user {
              id
              firstName
              profileImageUrl
            }
          }
        }
        pageInfo {
          totalSize
        }
      }
    }
  }
`);

const EVENT_QUERY = gql(`
  query GetEvent($params: EventParams!) {
    event(params: $params) {
      ...Event
    }
  }
`);

const TICKET_STATUS_QUERY = gql(`
  query PlanTicketStatus($params: EventParams!) {
    event(params: $params) {
      id
      myTicketConfirmed
    }
  }
`);

const AUTOLINK_URL_RE = /https?:\/\/[^\s<>[\](){}'"]+[^\s<>[\](){}'".,;:!?]/g;

type RenderedLinkPreview = { url: string; title?: string | null };

function renderAutolinkedText(
  text: string,
  linkPreviews: readonly RenderedLinkPreview[] | null | undefined,
): React.ReactNode[] {
  const titleByUrl = new Map<string, string>();
  for (const preview of linkPreviews ?? []) {
    if (preview.title && preview.title.trim()) {
      titleByUrl.set(preview.url, preview.title.trim());
    }
  }

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of text.matchAll(AUTOLINK_URL_RE)) {
    const url = match[0];
    const index = match.index ?? 0;
    if (index > lastIndex) {
      nodes.push(text.slice(lastIndex, index));
    }
    const display = titleByUrl.get(url) ?? url;
    nodes.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-butter-green underline underline-offset-2"
      >
        {display}
      </a>,
    );
    lastIndex = index + url.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function stripTeamId(appId: string): string {
  const parts = appId.split(".");
  if (parts.length <= 1) {
    return appId;
  }
  return parts.slice(1).join(".");
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const eventId = params.eventId;

  const url = new URL(request.url);
  const type = url.searchParams.get("type") as "plan-invite" | null;
  const code = url.searchParams.get("code");
  const inviterUserId = url.searchParams.get("inviterUserId");
  const { utmParams, session, needsCommit } =
    await getUTMParamsWithSession(request);

  const headers: HeadersInit = { "Cache-Control": "private, no-store" };
  if (needsCommit) {
    headers["Set-Cookie"] = await commitSession(session);
  }

  if (!eventId) {
    return data(
      {
        plan: null,
        error: "Plan ID is required",
        ticketStatus: null,
        authenticated: false,
        userExists: false,
        userAnonymous: false,
        type: null,
        openAppLink: "",
        iosAppId: stripTeamId(process.env.IOS_APP_ID || ""),
        androidAppId: process.env.ANDROID_APP_ID,
        stripePublishableKey: process.env.STRIPE_PUBLISHABLE_KEY,
      },
      { status: 500, headers },
    );
  }

  const openAppLink =
    type === "plan-invite" && code && inviterUserId
      ? buildAppsFlyerLink(
          {
            type: "event-invite",
            eventId,
            code,
            inviterUserId: inviterUserId,
          },
          utmParams,
        )
      : buildAppsFlyerLink({ type: "event", eventId }, utmParams);

  try {
    let { client, authenticated } = await createApolloClient(request);
    let ticketStatus: TicketStatus | null = null;
    if (url.searchParams.get("status") === "going") {
      ticketStatus = { state: "login" };
      if (authenticated) {
        try {
          const result = await client.query({
            query: TICKET_STATUS_QUERY,
            variables: { params: { id: eventId } },
          });
          ticketStatus = {
            state: result.data?.event?.myTicketConfirmed === true
              ? "confirmed"
              : "unavailable",
          };
        } catch (error) {
          const unauthenticated =
            (CombinedGraphQLErrors.is(error) && error.errors.some(error => error.extensions?.code === "UNAUTHENTICATED")) ||
            (ServerError.is(error) && error.statusCode === 401);
          if (unauthenticated) {
            // Remove an expired cookie before the public read and the next OTP request.
            session.unset("auth");
            headers["Set-Cookie"] = await commitSession(session);
            const publicRequest = new Request(request);
            publicRequest.headers.delete("Cookie");
            ({ client, authenticated } = await createApolloClient(publicRequest));
          } else {
            console.error("Error checking ticket status:", error);
            ticketStatus = { state: "error" };
          }
        }
      }
    }

    const [eventQuery, userQuery] = await Promise.all([
      client.query({
        query: EVENT_QUERY,
        variables: { params: { id: eventId } },
      }),
      authenticated
        ? client.query({ query: USER_EXISTS_QUERY }).catch(() => ({
            data: null as null | {
              user: { id: string; anonymous: boolean } | null;
            },
          }))
        : Promise.resolve({
            data: null as null | {
              user: { id: string; anonymous: boolean } | null;
            },
          }),
    ]);

    if (eventQuery.error) throw eventQuery.error;
    if (eventQuery.data?.event === undefined) throw new Error("Plan response was incomplete");

    let event = getFragmentData(EventFragmentDoc, eventQuery.data?.event);
    const requestedRegistrationID = url.searchParams.get("registrationID");
    if (event && authenticated && requestedRegistrationID && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requestedRegistrationID)) {
      try {
        const booking = await client.query({ query: READ_BOOKING, variables: { id: requestedRegistrationID }, fetchPolicy: "no-cache" });
        const registration = getFragmentData(PURCHASER_BOOKING, booking.data?.eventRegistration);
        if (registration?.eventID === eventId && registration.purchaser?.id === userQuery.data?.user?.id) event = { ...event, myRegistration: registration };
      } catch { /* Optional history lookup must not prevent viewing the plan. */ }
    }
    const userExists = !!userQuery.data?.user?.id;
    const userAnonymous = userQuery.data?.user?.anonymous === true;

    if (!event) {
      return data(
        {
          plan: null,
          error: "Plan not found",
          ticketStatus,
          authenticated,
          userExists,
          userAnonymous,
          type,
          openAppLink,
          iosAppId: stripTeamId(process.env.IOS_APP_ID || ""),
          androidAppId: process.env.ANDROID_APP_ID,
          stripePublishableKey: process.env.STRIPE_PUBLISHABLE_KEY,
        },
        { status: 404, headers },
      );
    }

    return data(
      {
        plan: event,

        error: null,
        ticketStatus,
        authenticated,
        userExists,
        userAnonymous,
        type,
        openAppLink,
        iosAppId: stripTeamId(process.env.IOS_APP_ID || ""),
        androidAppId: process.env.ANDROID_APP_ID,
        stripePublishableKey: process.env.STRIPE_PUBLISHABLE_KEY,
      },
      { headers },
    );
  } catch (error) {
    console.error("Error fetching plan:", error);

    return data(
      {
        plan: null,
        error: "Error occurred loading the plan",
        ticketStatus: null,
        authenticated: false,
        userExists: false,
        userAnonymous: false,
        type: null,
        openAppLink: "",
        iosAppId: stripTeamId(process.env.IOS_APP_ID || ""),
        androidAppId: process.env.ANDROID_APP_ID,
        stripePublishableKey: process.env.STRIPE_PUBLISHABLE_KEY,
      },
      { status: 500, headers },
    );
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

const CHECKOUT_PARAMS = new Set([
  "step",
  "discountCode",
  TICKET_VARIATION_PARAM,
]);

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  formMethod,
  defaultShouldRevalidate,
}) => {
  // Form submissions (incl. fetcher actions like the checkout sign-in)
  // can change auth state, so always revalidate after them.
  if (formMethod) return defaultShouldRevalidate;

  // Programmatic revalidations (useRevalidator) — URL is unchanged but the
  // caller explicitly wants the loader to re-run (e.g. checkout back button
  // after clearing the session cookie).
  if (currentUrl.toString() === nextUrl.toString()) {
    return defaultShouldRevalidate;
  }

  if (currentUrl.pathname !== nextUrl.pathname) {
    return defaultShouldRevalidate;
  }

  const stripped = (url: URL) => {
    const params = new URLSearchParams(url.searchParams);
    for (const key of CHECKOUT_PARAMS) params.delete(key);
    params.sort();
    return params.toString();
  };

  if (stripped(currentUrl) !== stripped(nextUrl)) {
    return defaultShouldRevalidate;
  }

  return false;
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data?.error || !data?.plan) {
    return [
      { title: data?.error === "Plan not found" ? "Plan Not Found | Butter" : "Plan Unavailable | Butter" },
      {
        name: "description",
        content: data?.error === "Plan not found" ? "The requested plan could not be found." : "We couldn’t load this plan. Please try again.",
      },
    ];
  }

  const description = data.plan.description.substring(0, 160);
  const url = `https://app.joinbutter.co/plans/${data.plan.id}`;
  const eventJsonLd = buildEventJsonLd(data.plan, url);

  return [
    { title: `${data.plan.title} | Butter` },
    { name: "description", content: description },
    // Open Graph
    { property: "og:title", content: data.plan.title },
    { property: "og:description", content: description },
    { property: "og:image", content: data.plan.bannerImageUrl },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    { property: "og:site_name", content: "Butter" },
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: data.plan.title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: data.plan.bannerImageUrl },
    // Canonical URL
    { tagName: "link", rel: "canonical", href: url },
    // Schema.org Event JSON-LD for Google rich results (BUT-1726)
    ...(eventJsonLd ? [{ "script:ld+json": eventJsonLd }] : []),
  ];
};

export type PlanActions = {
  "download-app": undefined;
  "private-guest-list": undefined;
  "private-location": undefined;
  "private-activity": undefined;
  "view-event-host": undefined;
  rsvp: undefined;
  "see-other-plans": undefined;
};

export default function PlanPage() {
  return <PlanPageContent data={useLoaderData<typeof loader>()} />;
}

export function PlanPageContent({ data, showCheckout = true }: {
  data: Awaited<ReturnType<typeof loader>>["data"];
  showCheckout?: boolean;
}) {
  const locale = useRootLocale();
  const revalidator = useRevalidator();
  const regionalCopy = getRegionalCopy(locale);
  const {
    plan,
    error,
    type,
    openAppLink,
    authenticated,
    userExists,
    userAnonymous,
    stripePublishableKey,
    ticketStatus,
  } = data;
  const [searchParams] = useSearchParams();
  const viewerID = useUser().user?.id;
  const captureAppHandoff = useAppHandoff(data.plan?.id, viewerID);
  const showingTicketStatus = searchParams.get("status") === "going";
  const { action, setAction } = useAction<PlanActions>();
  const posthogBootstrap = useRootPosthogBootstrap();
  const [guestListFlagAssignment, setGuestListFlagAssignment] = useState<{
    planId: string;
    distinctID: string;
    exposed: boolean;
  }>();
  const [placeIsSaved, setPlaceIsSaved] = useState(type === "plan-invite");
  const [checkoutWalletIntentID, setCheckoutWalletIntentID] = useState("");

  // Preload Stripe.js as soon as the plan page mounts so opening the
  // checkout dialog doesn't pay the SDK download cost.
  useEffect(() => {
    if (stripePublishableKey) getStripePromise(stripePublishableKey);
  }, [stripePublishableKey]);

  const planIsTicketed = plan?.type === EventType.Ticketed;
  const ticketVariationsEnabled = (plan?.ticketVariations.length ?? 0) > 0;
  const currentTicketVariation =
    plan && ticketVariationsEnabled
      ? getSelectedTicketVariation(plan, null)
      : undefined;
  const ticketVariationsUnavailable =
    ticketVariationsEnabled && currentTicketVariation === undefined;
  const ticketSalesAvailable = plan
    ? isTicketCheckoutAvailable(plan)
    : false;
  const planIsAttending = plan?.isAttending ?? false;
  const planIsHosting = plan?.isHosting ?? false;
  const planId = plan?.id;
  const distinctID = posthogBootstrap?.distinctID;
  // Evaluate only for ticketed visitors, not globally during SSR. Keep each
  // assignment tied to the plan and viewer so navigation starts hidden.
  useEffect(() => {
    if (!planId || !distinctID || !planIsTicketed || planIsAttending || planIsHosting) return;
    return posthog.onFeatureFlags((flags) => {
      setGuestListFlagAssignment({
        planId,
        distinctID,
        exposed:
          flags.includes("TICKETED_GUEST_LIST_VISIBILITY") &&
          posthog.getFeatureFlag("TICKETED_GUEST_LIST_VISIBILITY") === "test",
      });
    });
  }, [planId, distinctID, planIsTicketed, planIsAttending, planIsHosting]);
  const unclaimedGuestCount =
    plan?.unclaimedGuests?.pageInfo?.totalSize ?? plan?.unclaimedGuests?.edges?.length ?? 0;
  const planAtCapacity = plan
    ? plan.attendees.edges.length + unclaimedGuestCount >= plan.capacity
    : false;
  const ticketSalesAtCapacity = planAtCapacity || ticketVariationsUnavailable;
  const ticketCheckoutAvailable =
    ticketSalesAvailable &&
    (!ticketSalesAtCapacity || !!(plan?.approveGuests && plan.myRequest?.submittedAt));

  // Fire once per event id — derived props (auth, attending, capacity)
  // are snapshots of the page-load view, not signals to re-fire on.
  useEffect(() => {
    if (!planId || !plan) return;
    track("event_viewed", {
      event_id: planId,
      event_title: plan.title,
      event_type: planIsTicketed ? "ticketed" : "free",
      is_authenticated: authenticated,
      is_attending: planIsAttending,
      at_capacity: planAtCapacity,
      has_club: !!plan.club,
      has_invite_token: type === "plan-invite",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const ownedGroupBooking = isActivePurchaserGroupBooking(plan?.myRegistration, viewerID);
  const recipientReservation = plan?.myRequest?.reservation;
  const hasClaimedRequest = planIsTicketed && !!recipientReservation && !recipientReservation.isPurchaser;
  const initialCheckoutStep = ownedGroupBooking ? "status" : plan?.approveGuests
    ? plan.myRequest?.submittedAt
      ? "status"
      : plan.myRequest?.ticketPurchase
        ? "payment"
        : "tickets"
    : "tickets";
  const checkoutSearchParams = new URLSearchParams({
    step: initialCheckoutStep,
  });
  const draftVariationID = plan?.approveGuests
    ? plan.myRequest?.ticketPurchase?.variation?.id
    : undefined;
  if (draftVariationID) {
    checkoutSearchParams.set(TICKET_VARIATION_PARAM, draftVariationID);
  }
  const ticketedCheckoutTo = `?${checkoutSearchParams.toString()}`;
  const planCtaTo = ownedGroupBooking ? { search: "?step=status" } : hasClaimedRequest
    ? { pathname: `/plans/${plan?.id}/tickets/claim` }
    : checkoutWalletIntentID
    ? { search: "?step=tickets" }
    : planIsAttending
    ? { pathname: `/plans/${plan?.id}/app` }
    : planIsTicketed && ticketCheckoutAvailable
      ? { search: ticketedCheckoutTo }
      : { pathname: `/plans/${plan?.id}/app` };

  const ctaIsCheckout =
    !!ownedGroupBooking || !!checkoutWalletIntentID || (!planIsAttending && planIsTicketed && ticketCheckoutAvailable);
  const ctaOpensApp = !hasClaimedRequest && !ctaIsCheckout;
  const handleAppCta = () => {
    if (!planIsTicketed && (isDesktopDevice() || openAppLink)) captureAppHandoff();
    if (isDesktopDevice()) {
      setAction(undefined, { type: "rsvp", data: undefined });
    } else if (openAppLink) {
      track("app_download_clicked", {
        direction: "events",
        surface: "plan_main_cta",
      });
      window.location.assign(openAppLink);
    }
  };
  const appCtaHandler = ctaOpensApp ? handleAppCta : undefined;
  const handleSeeOtherPlans = () => {
    if (isDesktopDevice()) {
      setAction(undefined, { type: "see-other-plans", data: undefined });
    } else if (openAppLink) {
      track("app_download_clicked", {
        direction: "events",
        surface: "plan_see_other_plans",
      });
      window.location.assign(openAppLink);
    }
  };


  if (error || plan === null) {
    const notFound = error === "Plan not found";
    return (
      <div className="rounded-lg max-w-[800px] w-full text-center">
        <Text variant="primary" size="lg" as="h1" className="mt-8">
          {notFound ? "Plan not found" : "We couldn’t load this plan"}
        </Text>
        <Text variant="secondary" size="lg" className="mb-8">
          {notFound ? <>This plan was deleted or doesn’t exist. <a href="https://joinbutter.co" className="underline">Return to Butter</a></> : "Please try again in a moment."}
        </Text>
        {!notFound && <PrimaryButton type="button" disabled={revalidator.state !== "idle"} loading={revalidator.state !== "idle"} onClick={() => void revalidator.revalidate()} className="max-w-xs">
          Try again
        </PrimaryButton>}
      </div>
    );
  }

  const isTicketed = planIsTicketed;
  const isAttending = planIsAttending;
  const guestListExposed =
    isTicketed &&
    !isAttending &&
    !planIsHosting &&
    guestListFlagAssignment?.planId === plan.id &&
    guestListFlagAssignment?.distinctID === distinctID &&
    guestListFlagAssignment.exposed;
  const atCapacity = ticketSalesAtCapacity;
  const exactDetailsHidden = plan.location.exactDetailsHidden;
  const isGeneralArea = plan.location.isGeneralArea;
  const locationName = isGeneralArea
    ? "General area"
    : exactDetailsHidden
      ? "Private location"
      : plan.location.name;
  const exactFormattedAddress = plan.location.formattedAddress;
  const generalLocationSummary = [
    plan.location.suburb || plan.location.city || plan.location.state,
    plan.location.suburb
      ? plan.location.country === "United States"
        ? plan.location.city || plan.location.state
        : plan.location.state
      : undefined,
  ]
    .filter((part): part is string => Boolean(part))
    .join(", ");
  const exactLocationSummary = isGeneralArea
    ? generalLocationSummary
    : exactFormattedAddress || generalLocationSummary;
  const attendees = plan.attendees.edges;

  const waitlistSize =
    plan.requests.edges.filter((request) => request.node.accepted).length ?? 0;

  const primaryHost = plan.hosts.find((h) => h.primary)?.user;

  return (
    <main className="max-w-[1000px] w-full text-butter-green pb-3 lg:pb-0">
      <Outlet />

      {/* Mobile-only: full-width banner at the very top */}
      <div className="relative mb-3 lg:hidden">
        <ImageCarousel
          imageUrls={plan.bannerImageUrls ?? []}
          fallbackUrl={plan.bannerImageUrl || undefined}
          alt={plan.title}
        />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start lg:gap-8">
        {/* Desktop-only sidebar */}
        <aside className="hidden lg:flex lg:w-[360px] lg:flex-shrink-0 lg:sticky lg:top-[101px] flex-col gap-6">
          <div className="relative">
            <ImageCarousel
              imageUrls={plan.bannerImageUrls ?? []}
              fallbackUrl={plan.bannerImageUrl || undefined}
              alt={plan.title}
            />
          </div>

          <UrgencyPanel
            hasBooking={!!ownedGroupBooking}
            plan={plan}
            isAttending={isAttending}
            atCapacity={atCapacity}
            waitlistSize={waitlistSize}
            to={planCtaTo}
            onClick={appCtaHandler}
            onSeeOtherPlans={handleSeeOtherPlans}
            showPrivateGuestList={
              isTicketed && !isAttending && !guestListExposed
            }
            showExposedGuestList={
              isTicketed && !isAttending && guestListExposed
            }
            ticketVariationsEnabled={ticketVariationsEnabled}
          />
        </aside>

        {/* Right column — main content */}
        <div className="butter-reveal flex-1 min-w-0 mt-2 lg:mt-0 lg:max-w-[600px] flex flex-col gap-8">
          <header className="flex flex-col gap-2">
            {primaryHost &&
              (plan.club ? (
                <Link
                  to={`/clubs/${plan.club.id}`}
                  className="group mb-2 inline-flex items-center gap-1 self-start hover:opacity-80"
                >
                  <Text as="span" variant="secondary" size="sm" weight="bold">
                    Planned by {plan.club.name}
                  </Text>
                  {(plan.club.premium || plan.club.foundingMember) && (
                    <img
                      src={GoldenButterImage}
                      alt=""
                      className="h-4 w-4 flex-shrink-0"
                      aria-hidden
                    />
                  )}
                  <FiChevronRight
                    size={16}
                    className="text-butter-green transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setAction(undefined, {
                      type: "view-event-host",
                      data: undefined,
                    })
                  }
                  className="group mb-2 inline-flex items-center gap-1 self-start hover:opacity-80"
                >
                  <Text as="span" variant="secondary" size="sm" weight="bold">
                    Planned by {primaryHost.name}
                  </Text>
                  {primaryHost.foundingMember && (
                    <img
                      src={GoldenButterImage}
                      alt=""
                      className="h-4 w-4 flex-shrink-0"
                      aria-hidden
                    />
                  )}
                  <FiChevronRight
                    size={16}
                    className="text-butter-green transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </button>
              ))}
            <Text variant="primary" size="xl" as="h1">
              {plan.title}
            </Text>
            {(plan.tone || plan.structure) && (
              <div className="mt-1">
                <WhatToExpectPills
                  tone={plan.tone}
                  structure={plan.structure}
                />
              </div>
            )}
          </header>

          <div className="-mt-1 flex flex-wrap items-start gap-x-5 gap-y-2">
            <span className="inline-flex items-stretch gap-2">
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-butter-cream/20 from-[25%] to-butter-cream">
                <FiCalendar
                  size={22}
                  className="text-butter-green"
                  aria-hidden
                />
              </span>
              <Text as="span" variant="secondary" size="md">
                <span className="block font-semibold">
                  {formatLocalizedDate(plan.startDate, "eventDate", locale)}
                </span>
                <span className="block">
                  {formatLocalizedDate(
                    plan.startDate,
                    "time",
                    locale,
                  ).toLowerCase()}
                  {plan.endDate &&
                    ` – ${formatLocalizedDate(plan.endDate, "time", locale).toLowerCase()}`}
                </span>
              </Text>
            </span>
            {!exactDetailsHidden || isGeneralArea ? (
              <span className="inline-flex items-stretch gap-2">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-butter-cream/20 from-[25%] to-butter-cream">
                  <FiMapPin
                    size={22}
                    className="text-butter-green"
                    aria-hidden
                  />
                </span>
                <Text as="span" variant="secondary" size="md">
                  <span className="block font-semibold">{locationName}</span>
                  {exactLocationSummary &&
                    exactLocationSummary !== locationName && (
                      <span className="block">{exactLocationSummary}</span>
                    )}
                </Text>
              </span>
            ) : (
              <button
                type="button"
                onClick={() =>
                  setAction(undefined, {
                    type: "private-location",
                    data: undefined,
                  })
                }
                className="inline-flex items-stretch gap-2 self-start text-left hover:opacity-70"
              >
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-butter-cream/20 from-[25%] to-butter-cream">
                  <FiMapPin
                    size={22}
                    className="text-butter-green"
                    aria-hidden
                  />
                </span>
                <Text as="span" variant="secondary" size="md">
                  <span className="block font-semibold">Private location</span>
                  <span className="block">{generalLocationSummary}</span>
                </Text>
              </button>
            )}
          </div>

          {(plan.headline || plan.description || plan.richDescription) && (
            <section className="flex flex-col gap-2.5">
              {plan.headline && (
                <Text
                  variant="secondary"
                  size="md"
                  weight="bold"
                  className="leading-snug"
                >
                  {plan.headline.trim()}
                </Text>
              )}
              <RichDescription
                description={plan.description}
                richDescription={plan.richDescription}
              />
            </section>
          )}

          <EventInsight insight={plan.insight} />

          {isTicketed && plan.ticketInclusions.length > 0 && (
            <TicketInclusions inclusions={plan.ticketInclusions} />
          )}

          {plan.menuSample && plan.menuSample.trim().length > 0 && (
            <section className="mt-2 flex flex-col gap-1.5">
              <SectionHeading>The menu</SectionHeading>
              <Text
                variant="secondary"
                size="md"
                className="whitespace-pre-line leading-relaxed"
              >
                {renderAutolinkedText(plan.menuSample, plan.linkPreviews)}
              </Text>
            </section>
          )}

          <GuestList
            attendees={attendees}
            unclaimedGuestCount={unclaimedGuestCount}
            unclaimedGuests={plan.unclaimedGuests?.edges.map(({ node }) => node) ?? []}
            isTicketed={isTicketed}
            isAttending={isAttending}
            guestListExposed={guestListExposed}
            atCapacity={atCapacity}
            waitlistsEnabled={plan.waitlists}
            waitlistSize={waitlistSize}
            capacity={plan.capacity}
            onSetAction={setAction}
            ticketsLink={
              isTicketed && (ownedGroupBooking || ticketSalesAvailable) ? planCtaTo : undefined
            }
            ticketsDisabled={isTicketed && !ownedGroupBooking && !ticketSalesAvailable}
            onRsvp={!isTicketed && !isAttending ? appCtaHandler : undefined}
            onJoinWaitlist={
              !isAttending && atCapacity && plan.waitlists
                ? appCtaHandler
                : undefined
            }
            onSeeOtherPlans={handleSeeOtherPlans}
          />

          <section className="mt-2 flex flex-col gap-2">
            <SectionHeading>Location</SectionHeading>
            <div className="overflow-hidden rounded-lg border border-butter-cream">
              <PlanMap
                exactDetailsHidden={exactDetailsHidden}
                location={plan.location}
              />
              <div className="flex flex-col gap-4 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-butter-cream/20 from-[25%] to-butter-cream"
                  >
                    <FiMapPin
                      size={22}
                      className="text-butter-green"
                      aria-hidden
                    />
                  </span>
                  <div className="min-w-0">
                    <Text
                      variant="secondary"
                      size="md"
                      weight="bold"
                      className="leading-tight"
                    >
                      {locationName}
                    </Text>
                    {!exactDetailsHidden &&
                    !isGeneralArea &&
                    exactFormattedAddress ? (
                      <Text variant="secondary" size="sm" color="muted">
                        {exactFormattedAddress}
                      </Text>
                    ) : (
                      <Text variant="secondary" size="sm" color="muted">
                        {generalLocationSummary}
                      </Text>
                    )}
                  </div>
                </div>
                {isGeneralArea ? null : exactDetailsHidden ? (
                  atCapacity && plan.waitlists ? (
                    <PillButton
                      onClick={appCtaHandler}
                      variant="green-soft"
                      size="sm"
                      rightIcon={<FiChevronRight size={16} aria-hidden />}
                      className="flex-shrink-0 font-normal"
                    >
                      Join the waitlist to view location
                    </PillButton>
                  ) : ownedGroupBooking ? (
                    <PillButton to={planCtaTo} variant="green-soft" size="sm" rightIcon={<FiChevronRight size={16} aria-hidden />}>View booking</PillButton>
                  ) : atCapacity ? (
                    <PillButton
                      onClick={handleSeeOtherPlans}
                      variant="green-soft"
                      size="sm"
                      rightIcon={<FiChevronRight size={16} aria-hidden />}
                      className="flex-shrink-0 font-normal"
                    >
                      See what else is on
                    </PillButton>
                  ) : isTicketed && ticketCheckoutAvailable ? (
                    <PillButton
                      to={planCtaTo}
                      variant="green-soft"
                      size="sm"
                      rightIcon={<FiChevronRight size={16} aria-hidden />}
                      className="flex-shrink-0 font-normal"
                    >
                      Get tickets to view location
                    </PillButton>
                  ) : isTicketed ? null : (
                    <PillButton
                      onClick={() =>
                        setAction(undefined, {
                          type: "private-location",
                          data: undefined,
                        })
                      }
                      variant="green-soft"
                      size="sm"
                      rightIcon={<FiChevronRight size={16} aria-hidden />}
                      className="flex-shrink-0 font-normal"
                    >
                      RSVP to view location
                    </PillButton>
                  )
                ) : (
                  exactFormattedAddress && (
                    <PillButton
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            exactFormattedAddress,
                          )}`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                      variant="green-soft"
                      size="sm"
                      rightIcon={<FiChevronRight size={16} aria-hidden />}
                      className="flex-shrink-0 font-normal"
                    >
                      Get directions
                    </PillButton>
                  )
                )}
              </div>
            </div>
          </section>

          <HostCards
            hosts={plan.hosts}
            clubId={plan.club?.id}
            onSetAction={setAction}
          />

          <ActivityFeed
            plan={plan}
            isTicketed={isTicketed}
            isAttending={isAttending}
            guestListExposed={guestListExposed}
            atCapacity={atCapacity}
            onSetAction={setAction}
            onClick={appCtaHandler}
            onSeeOtherPlans={handleSeeOtherPlans}
            ticketsLink={
              isTicketed && (ownedGroupBooking || ticketSalesAvailable) ? planCtaTo : undefined
            }
            ticketsDisabled={isTicketed && !ownedGroupBooking && !ticketSalesAvailable}
          />
        </div>
      </div>

      <BottomCTA
        hasBooking={!!ownedGroupBooking}
        plan={plan}
        isAttending={isAttending}
        atCapacity={atCapacity}
        capacity={plan.capacity}
        attendeeCount={attendees.length}
        waitlistSize={waitlistSize}
        to={planCtaTo}
        onClick={appCtaHandler}
        onSeeOtherPlans={handleSeeOtherPlans}
        ticketVariationsEnabled={ticketVariationsEnabled}
      />

      {showCheckout && (showingTicketStatus ? (
        <TicketStatusDialog
          key={plan.id}
          eventId={plan.id}
          planTitle={plan.title}
          requiresApproval={plan.myRegistration?.status.approvalRequired ?? plan.approveGuests}
          hostName={primaryHost?.firstName}
          openAppLink={openAppLink}
          status={ticketStatus}
        />
      ) : (
        <CheckoutDialog
          onResumeChange={setCheckoutWalletIntentID}
          plan={plan}
          authenticated={authenticated}
          userExists={userExists}
          userAnonymous={userAnonymous}
          stripePublishableKey={stripePublishableKey}
          ticketVariationsEnabled={ticketVariationsEnabled}
        />
      ))}

      {!isTicketed && plan.similarEvents.length > 0 && (
        <div className="mt-12 mb-8 lg:mt-24">
          <SectionHeading>Other plans you might enjoy</SectionHeading>

          <div className="grid grid-cols-1 gap-6 mt-5 md:grid-cols-3 sm:grid-cols-2">
            {plan.similarEvents.map((similarPlan) => {
              const primaryHost = similarPlan.hosts.find(
                (h) => h.primary,
              )?.user;
              const isTicketedSimilar = similarPlan.type === EventType.Ticketed;
              const attendeeNodes = similarPlan.attendees.edges.map(
                (edge) => edge.node.user,
              );
              const totalAttendees =
                similarPlan.attendees.pageInfo.totalSize ??
                attendeeNodes.length;
              const placesLeftSimilar = Math.max(
                0,
                similarPlan.capacity - totalAttendees,
              );
              const atCapacitySimilar = totalAttendees >= similarPlan.capacity;
              return (
                <Link
                  key={similarPlan.id}
                  to={`/plans/${similarPlan.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg">
                    <div
                      className="w-full aspect-[434/372] bg-butter-cream bg-cover bg-center transition-transform duration-300 group-hover:scale-[1.03]"
                      style={{
                        backgroundImage: `url(${similarPlan.bannerImageUrl ? similarPlan.bannerImageUrl : "/images/default-plan-banner.png"})`,
                      }}
                    />
                    <span className="absolute top-2 left-2 inline-flex items-center rounded-md bg-butter-cream-light px-2.5 py-1 shadow-sm">
                      <Text as="span" variant="secondary" size="xs">
                        {formatEventDateTime(similarPlan.startDate, locale)}
                      </Text>
                    </span>
                  </div>
                  <div className="pt-2">
                    <div className="mb-2 flex items-center gap-2">
                      {primaryHost && (
                        <Avatar
                          user={primaryHost}
                          size={28}
                          avatarTextSize="xs"
                        />
                      )}
                      <Text variant="secondary" size="sm">
                        Planned by {primaryHost?.name ?? "Unknown"}
                      </Text>
                    </div>
                    <Text
                      variant="primary"
                      size="sm"
                      as="h3"
                      className="mb-1 line-clamp-1"
                    >
                      {similarPlan.title}
                    </Text>
                    {isTicketedSimilar ? (
                      <span className="inline-flex items-center rounded-md bg-butter-cream px-2.5 py-1">
                        <Text as="span" variant="secondary" size="sm">
                          🤫 Private guest list
                        </Text>
                      </span>
                    ) : (
                      <div className="flex items-center gap-2 min-w-0">
                        {attendeeNodes.length > 0 && (
                          <div className="flex -space-x-2 flex-shrink-0">
                            {attendeeNodes.slice(0, 3).map((user) => (
                              <Avatar
                                key={user.id}
                                user={user}
                                size={28}
                                rounded
                                className="border-2 border-butter-cream-light"
                              />
                            ))}
                          </div>
                        )}
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                          {totalAttendees > 0 && (
                            <>
                              <Text as="span" variant="secondary" size="sm">
                                {totalAttendees}{" "}
                                {totalAttendees === 1 ? "person" : "people"}{" "}
                                going
                              </Text>
                              <GoDotFill
                                size={6}
                                className="text-butter-green"
                                aria-hidden
                              />
                            </>
                          )}
                          <Text as="span" variant="secondary" size="sm">
                            {atCapacitySimilar
                              ? similarPlan.waitlists
                                ? "Join the waitlist"
                                : "Event is full"
                              : formatPlacesLeft(placesLeftSimilar, locale)}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <CTADialog
        open={action?.type === "download-app"}
        onClose={() => setAction("download-app", undefined)}
        link={openAppLink}
        title="The club doesn't stop between events"
        description="The app is where the club actually lives — group chats, DMs with members, and plans before they go public."
        iconUrl={GreenFlowerImage}
        buttonLabel="Get the app"
        tracking={{ direction: "events", surface: "plan_download_app_modal" }}
      />

      <CTADialog
        open={action?.type === "private-guest-list"}
        onClose={() => setAction("private-guest-list", undefined)}
        link={openAppLink}
        buttonTo={isTicketed && (ownedGroupBooking || ticketCheckoutAvailable) ? planCtaTo : undefined}
        buttonDisabled={isTicketed && !ownedGroupBooking && !ticketCheckoutAvailable}
        title="Private guest list"
        description="Only attendees can see who's going."
        qrTitle="Only attendees can see who's going"
        iconUrl={RedFlowerImage}
        buttonLabel={ownedGroupBooking ? "View booking" : isTicketed ? "Get tickets" : "Get the app to view"}
        tracking={{
          direction: "events",
          surface: "plan_private_guest_list_modal",
        }}
      />

      <CTADialog
        open={action?.type === "private-activity"}
        onClose={() => setAction("private-activity", undefined)}
        link={openAppLink}
        title="Private activity"
        description={
          guestListExposed && isTicketed
            ? "Only attendees can see who's saving and joining the waitlist."
            : "Only attendees can see who’s going, saving and joining the waitlist."
        }
        qrTitle="Only attendees can see activity"
        iconUrl={RedFlowerImage}
        buttonLabel="Get the app to join"
        tracking={{
          direction: "events",
          surface: "plan_private_activity_modal",
        }}
      />

      <CTADialog
        open={action?.type === "private-location"}
        onClose={() => setAction("private-location", undefined)}
        link={openAppLink}
        buttonTo={isTicketed && (ownedGroupBooking || ticketCheckoutAvailable) ? planCtaTo : undefined}
        buttonDisabled={isTicketed && !ownedGroupBooking && !ticketCheckoutAvailable}
        title="Exact location shared with confirmed guests"
        description="You’ll see the venue and directions once your spot is confirmed."
        qrTitle="Exact location shared with confirmed guests"
        iconUrl={RedFlowerImage}
        buttonLabel={ownedGroupBooking ? "View booking" : isTicketed ? "Get tickets" : "Get the app to join"}
        tracking={{
          direction: "events",
          surface: "plan_private_location_modal",
        }}
      />

      <CTADialog
        open={action?.type === "view-event-host"}
        onClose={() => setAction("view-event-host", undefined)}
        link={openAppLink}
        buttonTo={isTicketed && (ownedGroupBooking || ticketCheckoutAvailable) ? planCtaTo : undefined}
        buttonDisabled={isTicketed && !ownedGroupBooking && !ticketCheckoutAvailable}
        title="Private profile"
        description="Only attendees can see the planner’s full profile, and chat with them directly."
        qrTitle="Private profile"
        iconUrl={RedFlowerImage}
        buttonLabel={ownedGroupBooking ? "View booking" : isTicketed ? "Get tickets" : "Get the app"}
        tracking={{
          direction: "events",
          surface: "plan_view_event_host_modal",
        }}
      />

      <CTADialog
        open={action?.type === "rsvp"}
        onClose={() => setAction("rsvp", undefined)}
        link={openAppLink}
        forceLayout="qr"
        title="You're one scan away"
        description="Scan the code to download Butter and RSVP — takes about 30 seconds."
        qrTitle="You're one scan away"
        tracking={{ direction: "events", surface: "plan_rsvp_modal" }}
      />

      <CTADialog
        open={action?.type === "see-other-plans"}
        onClose={() => setAction("see-other-plans", undefined)}
        link={openAppLink}
        forceLayout="qr"
        title="Explore 300+ plans happening this month"
        description="Scan to join, or copy the link to your phone."
        qrTitle="Explore 300+ plans happening this month"
        tracking={{
          direction: "events",
          surface: "plan_see_other_plans_modal",
        }}
      />

      <CTADialog
        open={placeIsSaved}
        onClose={() => setPlaceIsSaved(false)}
        link={openAppLink}
        title={`We've saved your ${regionalCopy.place}`}
        description={`Confirm your ${regionalCopy.place}, see who else is going, and join the group chat in the app.`}
        iconUrl={GreenFlowerImage}
        buttonLabel="Get the app to join"
        tracking={{ direction: "events", surface: "plan_place_saved_modal" }}
      />

      <UrgencyPanel
            hasBooking={!!ownedGroupBooking}
        plan={plan}
        isAttending={isAttending}
        atCapacity={atCapacity}
        waitlistSize={waitlistSize}
        to={planCtaTo}
        onClick={appCtaHandler}
        onSeeOtherPlans={handleSeeOtherPlans}
        className="sticky bottom-3 z-30 mt-8 lg:hidden"
        ticketVariationsEnabled={ticketVariationsEnabled}
      />
    </main>
  );
}
